/**
 * Free Gift With Purchase — extended
 *
 * Shows an in-cart gift picker once the qualifying subtotal meets the configured threshold, and
 * keeps the cart in sync with the customer's choice.
 *
 * Rules:
 * - Below the threshold: the picker is hidden and any gift line is removed.
 * - At or above the threshold with no gift chosen yet: the picker is shown. NOTHING is added
 *   automatically — the customer picks a colour and size.
 * - Once a gift is in the cart the picker is hidden again; the gift line is the confirmation.
 * - Only one gift can exist at a time. Changing any part of the selection removes the old line
 *   before adding the new one, so two gifts never coexist even transiently.
 * - The choice is never remembered (no storage of any kind): if the cart drops below the
 *   threshold the gift is removed, and re-crossing the threshold shows the picker afresh.
 *
 * The picker markup (snippets/cart-free-gift-picker.liquid) is rendered inside the `line-items`
 * part of cart-line-items.liquid, so the theme's Section Rendering API re-renders it on every cart
 * change. Active style/colour/size state is therefore server-derived — this file never has to
 * re-hydrate it, only toggle visibility and issue cart mutations.
 *
 * Config comes from window.freeGiftConfig (set in layout/theme.liquid).
 * Currency conversion uses window.Shopify.currency.rate (Shopify Markets).
 */
(function () {
  'use strict';

  const config = window.freeGiftConfig;
  if (!config || !config.enabled) return;

  const GIFT_PROPERTY = '_is_free_gift';
  const JSON_HEADERS = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };

  const state = {
    busy: false,
    queued: null,
    evaluateTimeout: null,
    // Last known threshold verdict. Applied optimistically the moment the cart re-renders so the
    // picker doesn't blink out of existence during the debounce window.
    overThreshold: false,
    // Survives the re-render that a mutation triggers, so the customer actually reads it.
    hint: '',
    // The colour the customer last clicked. The theme swaps in a freshly server-rendered picker on
    // every cart change, and Liquid can only pre-select the FIRST colour while no gift is in the
    // cart — so without remembering this, the selection silently jumps back to colour one.
    selectedProductId: '',
    // The customer clicked "Change gift" on the gift line: keep the picker open even though a gift
    // is in the cart, until they pick another variant, cancel, or the gift disappears.
    changing: false
  };

  // --- DOM helpers ---------------------------------------------------------

  function picker() {
    return document.querySelector('[data-free-gift-picker]');
  }

  /**
   * Is this variant still selectable? The picker only renders available variants of available
   * sibling products, so absence from the DOM is the signal that it sold out or was withdrawn.
   */
  function variantIsOffered(el, variantId) {
    return !!el.querySelector('[data-gift-variant="' + variantId + '"]');
  }

  function setBusy(isBusy) {
    const el = picker();
    if (!el) return;
    el.classList.toggle('is-busy', isBusy);
  }

  function setPickerVisible(visible) {
    const el = picker();
    if (!el) return;
    if (visible) {
      el.removeAttribute('hidden');
    } else {
      el.setAttribute('hidden', '');
    }
  }

  function setHint(message) {
    state.hint = message || '';
    renderHint();
  }

  function renderHint() {
    const el = picker();
    if (!el) return;
    const hint = el.querySelector('[data-gift-hint]');
    if (!hint) return;
    if (state.hint) {
      hint.textContent = state.hint;
      hint.removeAttribute('hidden');
    } else {
      hint.setAttribute('hidden', '');
    }
  }

  /**
   * Make sure the card is never half-painted: if no colour is active (e.g. the gift line's product
   * dropped out of the rendered list), pre-select the first colour so image, title and size row
   * are all present. Liquid already pre-selects on first paint; this is the safety net. Visual
   * only — no gift is added until a size is chosen.
   */
  function syncPanels() {
    const el = picker();
    if (!el) return;

    const panel = el.querySelector('[data-gift-style-panel]');
    if (!panel) return;
    panel.removeAttribute('hidden');

    preloadColorImages();

    const swatches = panel.querySelectorAll('.cart-gift-picker__colors [data-gift-color]');

    // A gift in the cart makes the server-rendered active state authoritative — it is the real
    // selection. Otherwise restore what the customer clicked before the swap, falling back to
    // Liquid's first-colour default.
    if (!el.hasAttribute('data-gift-line-key') && state.selectedProductId) {
      for (let i = 0; i < swatches.length; i++) {
        if (swatches[i].getAttribute('data-gift-color') === state.selectedProductId) {
          selectColor(swatches[i]);
          return;
        }
      }
    }

    const hasActiveColor = panel.querySelector('.cart-gift-picker__colors [data-gift-color].is-active');
    if (!hasActiveColor && swatches.length) {
      selectColor(swatches[0]);
    }
  }

  /**
   * Mark a colour swatch active: swap the card image and title, and reveal that colour's size row.
   * Purely visual — no cart mutation.
   */
  function selectColor(swatch) {
    const panel = swatch.closest('[data-gift-style-panel]');
    if (!panel) return;

    const productId = swatch.getAttribute('data-gift-color');
    state.selectedProductId = productId;

    const swatches = panel.querySelectorAll('.cart-gift-picker__colors [data-gift-color]');
    for (let i = 0; i < swatches.length; i++) {
      swatches[i].classList.toggle('is-active', swatches[i] === swatch);
    }

    const image = panel.querySelector('[data-gift-main-image]');
    const imageSrc = swatch.getAttribute('data-gift-image');
    if (image && imageSrc) {
      image.src = imageSrc;
      image.removeAttribute('hidden');
    }

    const title = panel.querySelector('[data-gift-main-title]');
    const titleText = swatch.getAttribute('data-gift-title');
    if (title && titleText) title.textContent = titleText;

    // "Farve: Lemon" — the selected colour's name sits next to the label, like on the PDP.
    const colorLabel = panel.querySelector('[data-gift-color-label]');
    if (colorLabel) colorLabel.textContent = swatch.getAttribute('data-gift-color-name') || '';

    const rows = panel.querySelectorAll('[data-gift-size-row]');
    for (let r = 0; r < rows.length; r++) {
      if (rows[r].getAttribute('data-gift-size-row') === productId) {
        rows[r].removeAttribute('hidden');
      } else {
        rows[r].setAttribute('hidden', '');
      }
    }
  }

  // --- Cart API ------------------------------------------------------------

  function getCart() {
    return fetch(window.theme.routes.cart_url + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      return res.json();
    });
  }

  function isGiftItem(item) {
    return !!(item.properties && item.properties[GIFT_PROPERTY] === 'true');
  }

  function findGiftInCart(cart) {
    for (let i = 0; i < cart.items.length; i++) {
      if (isGiftItem(cart.items[i])) return cart.items[i];
    }
    return null;
  }

  /**
   * Convert the threshold from the store's primary currency to the presentment currency.
   * Cart prices are in subunits (øre/cents) of the presentment currency.
   */
  function getThresholdInSubunits() {
    const rate = (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    return Math.round(config.thresholdBase * rate * 100);
  }

  /**
   * Sum every line EXCEPT the gift, using line_price (pre-discount).
   *
   * Deliberately pre-discount so that it matches the discount function's cost.subtotalAmount, and
   * so a discount code cannot pull the customer under the threshold and yank the gift back out of
   * their cart mid-session.
   */
  function getQualifyingSubtotal(cart) {
    let total = 0;
    for (let i = 0; i < cart.items.length; i++) {
      if (isGiftItem(cart.items[i])) continue;
      total += cart.items[i].line_price;
    }
    return total;
  }

  function addGift(variantId, colorName) {
    const properties = {};
    properties[GIFT_PROPERTY] = 'true';
    if (config.colorLabel && colorName) properties[config.colorLabel] = colorName;

    return fetch(window.theme.routes.cart_add_url + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: properties }]
      })
    }).then(function (res) {
      // Unavailable variant (sold out between render and click) — fail silently.
      return res.ok ? res.json() : null;
    });
  }

  function changeLine(key, quantity) {
    return fetch(window.theme.routes.cart_change_url + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (res) {
      return res.ok ? res.json() : null;
    });
  }

  function triggerCartRefresh() {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  }

  // --- Mutation lock -------------------------------------------------------

  /**
   * Serialises cart mutations. A replace is two sequential round trips, and a customer can click
   * several swatches inside that window — so a click during a mutation is queued rather than
   * dropped, and only the LAST queued selection is applied.
   */
  function withLock(fn) {
    if (state.busy) return Promise.resolve(null);
    state.busy = true;
    setBusy(true);

    return fn()
      .catch(function () {
        return null;
      })
      .then(function (result) {
        state.busy = false;
        setBusy(false);

        const queued = state.queued;
        state.queued = null;
        if (queued) selectGift(queued.variantId, queued.colorName);

        return result;
      });
  }

  // --- Selection -----------------------------------------------------------

  function selectGift(variantId, colorName) {
    if (state.busy) {
      state.queued = { variantId: variantId, colorName: colorName };
      return;
    }

    withLock(function () {
      return getCart().then(function (cart) {
        const gift = findGiftInCart(cart);

        // Already the selected variant — nothing to do.
        if (gift && String(gift.variant_id) === String(variantId)) return null;

        // Remove FIRST so two gift lines can never coexist, even transiently.
        const chain = gift ? changeLine(gift.key, 0) : Promise.resolve(null);

        return chain
          .then(function () {
            return addGift(variantId, colorName);
          })
          .then(function (result) {
            if (!result) {
              // The add failed — re-evaluate so the picker comes back without the stale option.
              setHint(config.unavailableText);
              scheduleEvaluation();
              return null;
            }
            state.hint = '';
            state.changing = false;
            // Hide immediately rather than waiting for the re-render — the fresh copy is
            // rendered with the gift line baked in and stays hidden via onCartChange.
            setPickerVisible(false);
            triggerCartRefresh();
            return result;
          });
      });
    });
  }

  // --- Evaluation ----------------------------------------------------------

  function evaluateCart() {
    if (state.busy) return;

    getCart()
      .then(function (cart) {
        const threshold = getThresholdInSubunits();
        const subtotal = getQualifyingSubtotal(cart);
        const gift = findGiftInCart(cart);
        const el = picker();

        state.overThreshold = subtotal >= threshold;

        if (subtotal < threshold) {
          setPickerVisible(false);
          if (gift) {
            return withLock(function () {
              return changeLine(gift.key, 0).then(triggerCartRefresh);
            });
          }
          return null;
        }

        // No picker in the DOM. The cart drawer renders it on every page via the overlay section
        // group, so this normally only happens with an empty cart — but a template or section-group
        // change could also cause it. Deliberately do NOT touch the cart here: silently deleting a
        // valid gift because an element is missing is far worse than leaving it. A gift that has
        // genuinely become unselectable is caught by the variant check below instead.
        if (!el) return null;

        // "Change gift" mode only makes sense while a gift exists to change.
        if (!gift) state.changing = false;

        // A chosen gift hides the picker — the gift line itself is the confirmation. The picker
        // only shows while there is a choice left to make, or while the customer is changing it.
        setPickerVisible(!gift || state.changing);
        syncPanels();
        renderHint();

        if (gift && gift.quantity > 1) {
          return withLock(function () {
            return changeLine(gift.key, 1).then(triggerCartRefresh);
          });
        }

        // The chosen variant is no longer offered (sold out, or withdrawn from the configured
        // list). Remove it and say why — the customer picks again from what's left. The choice is
        // deliberately NOT remembered anywhere, so re-crossing the threshold always starts fresh.
        if (gift && !variantIsOffered(el, gift.variant_id)) {
          setHint(config.unavailableText);
          return withLock(function () {
            return changeLine(gift.key, 0).then(triggerCartRefresh);
          });
        }

        if (gift) {
          // A gift is present and valid — clear any stale "unavailable" notice.
          setHint('');
        }

        return null;
      })
      .catch(function () {
        // Fail silently — never break the cart experience.
      });
  }

  function scheduleEvaluation() {
    if (state.evaluateTimeout) clearTimeout(state.evaluateTimeout);
    state.evaluateTimeout = setTimeout(evaluateCart, 500);
  }

  // --- Events --------------------------------------------------------------

  // Delegated on document so it survives the [data-api-line-items] innerHTML swap, exactly like
  // the theme's own complementary-product selectors in custom.js.
  document.addEventListener('click', function (event) {
    const el = picker();

    // "Change gift" on the gift line re-opens the picker with the current choice pre-selected
    // (Liquid derives the active colour/size from the gift line on every render).
    const change = event.target.closest('[data-free-gift-change]');
    if (change) {
      event.preventDefault();
      if (!el) return;
      state.changing = true;
      setPickerVisible(true);
      syncPanels();
      renderHint();
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    if (!el || !el.contains(event.target)) return;

    const cancel = event.target.closest('[data-free-gift-cancel]');
    if (cancel) {
      event.preventDefault();
      state.changing = false;
      setPickerVisible(false);
      return;
    }

    const swatch = event.target.closest('.cart-gift-picker__colors [data-gift-color]');
    if (swatch) {
      event.preventDefault();
      selectColor(swatch);

      // A default-variant-only product has no size to pick — select it straight away.
      const directVariant = swatch.getAttribute('data-gift-variant');
      if (directVariant) {
        selectGift(directVariant, swatch.getAttribute('data-gift-color-name'));
      }
      return;
    }

    const size = event.target.closest('[data-gift-size-row] [data-gift-variant]');
    if (size) {
      event.preventDefault();
      if (size.classList.contains('is-disabled')) return;

      // Optimistic feedback: mark the clicked size active immediately instead of waiting for the
      // two cart round-trips and the section re-render. The server-rendered copy confirms it.
      const row = size.closest('[data-gift-size-row]');
      const options = row.querySelectorAll('[data-gift-variant]');
      for (let i = 0; i < options.length; i++) {
        options[i].classList.toggle('is-active', options[i] === size);
      }

      selectGift(size.getAttribute('data-gift-variant'), size.getAttribute('data-gift-color-name'));
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', evaluateCart);
  } else {
    evaluateCart();
  }

  /**
   * The theme dispatches these after it rebuilds the cart UI, which replaces the picker with a
   * fresh copy that Liquid renders hidden. Re-apply the last known verdict synchronously so the
   * picker doesn't blink out for the length of the debounce, then confirm it against the server.
   */
  function onCartChange() {
    // Only re-show when there is still a choice to make: the fresh Liquid copy carries
    // data-gift-line-key whenever a gift line exists, which is server-rendered truth.
    const el = picker();
    if (el && !el.hasAttribute('data-gift-line-key')) state.changing = false;
    if (state.overThreshold && el && (!el.hasAttribute('data-gift-line-key') || state.changing)) {
      setPickerVisible(true);
      syncPanels();
      renderHint();
    }
    scheduleEvaluation();
  }

  /**
   * The card image is a different CDN derivative from the swatch thumbnail (a different width, so a
   * different URL), which means the first click on each colour waited on a fresh image request
   * before anything visibly changed. Warming them makes colour switching feel instant.
   *
   * Runs on every re-render because the picker's DOM is replaced wholesale; the browser cache makes
   * repeat calls free.
   */
  function preloadColorImages() {
    const el = picker();
    if (!el) return;

    const swatches = el.querySelectorAll('.cart-gift-picker__colors [data-gift-color]');
    for (let i = 0; i < swatches.length; i++) {
      const src = swatches[i].getAttribute('data-gift-image');
      if (src) new Image().src = src;
    }
  }

  document.addEventListener('theme:cart:change', onCartChange);
  document.addEventListener('theme:product:added', onCartChange);
})();
